import * as React from 'react'

import {
  Alert,
  Box,
  Button,
  NumberBadge,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import QRCode from 'qrcode-svg'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FactorialIDApiError,
  TotpNewData,
  confirmTotpProvisioning,
  startTotpProvisioning,
} from 'modules/factorial_id/lib/api_client'

interface ProvisionMFAModalProps {
  onClose: () => void
  onSuccess: () => void
}

export function ProvisionMFAModal({ onClose, onSuccess }: ProvisionMFAModalProps) {
  const [qrCodeSVG, setQrCodeSVG] = React.useState<string>()
  const [mfaCode, setMfaCode] = React.useState<string>('')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    const initializeProvisioning = async () => {
      try {
        setLoading(true)
        setError(null)

        const provisioningData: TotpNewData = await startTotpProvisioning()

        const qrCodeObject = new QRCode({
          content: provisioningData.provisioning_uri,
          join: true,
          container: 'svg-viewbox',
          padding: 8,
        })

        setQrCodeSVG(qrCodeObject.svg().trim())
      } catch (err) {
        if (err instanceof FactorialIDApiError) {
          setError(err.message || 'Failed to initialize MFA setup')
        } else {
          setError(
            err instanceof FactorialIDApiError ? err.message : 'Failed to initialize MFA setup'
          )
        }
      } finally {
        setLoading(false)
      }
    }

    initializeProvisioning()
  }, [])

  const onMfaCodeChange = (value: string) => {
    setMfaCode(value)
    // Clear error when user starts typing a new code
    if (error) {
      setError(null)
    }
  }

  const onSubmit = async () => {
    if (!mfaCode) return

    try {
      setSubmitting(true)
      setError(null)

      await confirmTotpProvisioning(mfaCode)

      app.setFlash({
        type: 'correct',
        message: i18n.t('personal_settings.mfa.activated_successfully'),
      })

      onSuccess()
    } catch (err) {
      if (err instanceof FactorialIDApiError) {
        setError(err.message || 'An unexpected error occurred')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to enable MFA')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      illustration="lock-user"
      onClose={onClose}
      onSubmit={onSubmit}
      title={i18n.t('personal_settings.mfa.activate_modal_title')}
      description={i18n.t('personal_settings.mfa.activate_modal_description')}
      footer={
        <>
          <Button type="secondary" disabled={submitting} onClick={onClose}>
            {i18n.t('personal_settings.mfa.cancel')}
          </Button>
          <Button disabled={submitting || !mfaCode || loading} type="primary" submit>
            {i18n.t('personal_settings.mfa.activate')}
          </Button>
        </>
      }
    >
      <ActivationHelp />

      {error && <Alert color="error">{error}</Alert>}

      <Box height="s240" alignItems="center" justifyContent="center" padding="s24">
        {loading && <Spinner />}
        {qrCodeSVG && (
          <Box height="s240" width="s240">
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(qrCodeSVG)}`}
              alt="QR Code for MFA setup"
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}
      </Box>

      <Box>
        <TextField
          label={i18n.t('personal_settings.mfa.verification_code')}
          placeholder="123456"
          value={mfaCode}
          onChange={onMfaCodeChange}
        />
      </Box>
    </Modal>
  )
}

const ActivationHelp = () => {
  return (
    <Box flexDirection="column" gap="s12">
      <Box flexDirection="row" gap="s12">
        <NumberBadge label={1} />
        <Text size="200">
          {i18n.tView('personal_settings.mfa.activation_help.step_1', {
            android_store_link: (
              <a
                rel="nofollow noopener noreferrer"
                target="_blank"
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              >
                Android
              </a>
            ),

            ios_store_link: (
              <a
                rel="nofollow noopener noreferrer"
                target="_blank"
                href="https://apps.apple.com/app/google-authenticator/id388497605"
              >
                iOS
              </a>
            ),
          })}
        </Text>
      </Box>
      <Box flexDirection="row" gap="s12">
        <NumberBadge label={2} />
        <Text size="200">{i18n.t('personal_settings.mfa.activation_help.step_2')}</Text>
      </Box>
      <Box flexDirection="row" gap="s12">
        <NumberBadge label={3} />
        <Text size="200">{i18n.t('personal_settings.mfa.activation_help.step_3')}</Text>
      </Box>
    </Box>
  )
}
